import { asReadonlyBS } from '@ocodelib/ex-util/rx';
import initSwc, { transformSync } from '@swc/wasm-web';
import { BehaviorSubject } from 'rxjs';

export function makeJsConsoleHook() {
  const ctx = { canceled: false };
  const consoleHookScriptUrl$ = new BehaviorSubject<string | null>(null);

  // initSwc
  (async () => {
    await initSwc();
    if (ctx.canceled) return;
    const sourceCode = transformSync(CONSOLE_HOOK_SOURCE, {
      filename: '__console_hook.js',
    }).code;

    const blob = new Blob([sourceCode], { type: 'application/javascript' });
    consoleHookScriptUrl$.next(URL.createObjectURL(blob));
  })();

  function destroy() {
    ctx.canceled = true;
    const scriptUrl = consoleHookScriptUrl$.value;
    if (scriptUrl) {
      URL.revokeObjectURL(scriptUrl);
      consoleHookScriptUrl$.next(null);
    }
  }

  return {
    consoleHookScriptUrl$: asReadonlyBS(consoleHookScriptUrl$),
    destroy,
  };
}

const CONSOLE_HOOK_SOURCE = `
(function () {
  function safeStringifyReplacer(seen) {
    return function (key, value) {
      if (typeof value?.toJSON === "function") {
        value = value.toJSON();
      }
      if (!(value !== null && typeof value === "object")) {
        return value;
      }
      if (seen.has(value)) {
        return "[Circular]";
      }
      seen.add(value);
      const newValue = Array.isArray(value) ? [] : {};
      for (const [key2, value2] of Object.entries(value)) {
        newValue[key2] = safeStringifyReplacer(seen)(key2, value2);
      }
      seen.delete(value);
      return newValue;
    };
  }

  function safeStringify(object, { indentation } = {}) {
    const seen = new WeakSet();
    return JSON.stringify(object, safeStringifyReplacer(seen), indentation);
  }

  function stringifyArgs(args) {
    if (typeof args === "undefined" || args === null) return args;
    const result = [];
    for (let i = 0; i < args.length; i++) {
      result.push(safeStringify(args[i]));
    }
    return result;
  }

  function wrapConsole(method) {
    const nativeConsole = console[method];
    console[method] = function (...args) {
      parent.postMessage({ type: "console", method, value: stringifyArgs(args) });
      nativeConsole.apply(null, args);
    };
  }

  const methods = ["log","debug", "info", "warn", "error", "table", "clear", "time", "timeEnd", "count", "assert", "command", "result", "dir"];
  for (let i = 0; i < methods.length; i++) {
    wrapConsole(methods[i]);
  }

  const handleError = function (...args) {
    console.error.apply(null, args);
    return false;
  };

  onerror = handleError;
  window.addEventListener("error", handleError);
  onunhandledrejection = handleError;
})();
`;
